import { useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { ChatConversationList } from '@/modules/common/Chat/components/ChatConversationList';

import { useHouseholdChatContext } from '../contexts/householdChat.context';
import { useDeleteHouseholdChatConversationMutation } from '../graphql/HouseholdChat.generated';
import { useHouseholdChatConversations } from '../hooks/useHouseholdChatConversations';
import { DeleteConversationConfirmationModal } from './DeleteConversationConfirmationModal';
import {
  deriveConversationTitle,
  formatConversationTimestamp,
  isValidationQuestionsConversation,
} from './householdChatConversationList.utils';

export function HouseholdChatConversationList() {
  const {
    householdId,
    selectConversation,
    conversationId,
    startNewConversation,
  } = useHouseholdChatContext();
  const { conversations, hasMore, loadMore, loading, loadingMore, refetch } =
    useHouseholdChatConversations({ householdId });
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const [deleteConversation, { loading: isDeleting }] =
    useDeleteHouseholdChatConversationMutation({
      onError: (error) => {
        reportError('Failed to delete conversation', error);
        showFeedback('Failed to delete conversation.');
      },
      onCompleted: () => {
        showFeedback('Conversation deleted.', { variant: 'success' });
        // If the deleted conversation was the active one, reset to new
        if (pendingDeleteId === conversationId) {
          startNewConversation();
        }
        setPendingDeleteId(null);
        refetch();
      },
    });

  function handleDeleteRequest(id: string) {
    setPendingDeleteId(id);
  }

  function handleConfirmDelete() {
    if (!pendingDeleteId) return;
    void deleteConversation({ variables: { id: pendingDeleteId } });
  }

  function handleCancelDelete() {
    setPendingDeleteId(null);
  }

  // The rows already carry each conversation's kind, so pass it along: the chat
  // renders kind-dependent chrome (e.g. hiding the composer for validation
  // questions) correctly on the first frame instead of after the history loads.
  function handleSelectConversation(id: string) {
    const selected = conversations.find(
      (conversation) => conversation.id === id
    );
    selectConversation(id, selected?.kind ?? null);
  }

  return (
    <>
      <ChatConversationList
        conversations={conversations.map((conversation) => ({
          id: conversation.id,
          isDeletable: !isValidationQuestionsConversation(conversation),
          timestamp: formatConversationTimestamp(conversation.updatedAt),
          title: deriveConversationTitle(conversation),
        }))}
        emptyDescription="Start a new chat to ask about this household."
        emptyTitle="No conversations yet"
        hasMore={hasMore}
        loading={loading}
        loadingMore={loadingMore}
        onDeleteConversation={handleDeleteRequest}
        onLoadMore={() => {
          void loadMore();
        }}
        onSelectConversation={handleSelectConversation}
      />
      {pendingDeleteId && (
        <DeleteConversationConfirmationModal
          isDeleting={isDeleting}
          onClose={(reason) => {
            if (reason === 'confirm') {
              handleConfirmDelete();
            } else {
              handleCancelDelete();
            }
          }}
        />
      )}
    </>
  );
}
